export class SubscriptionPermissionService {
  
  static SUBSCRIPTION_TYPES = {
    FREE: 'free',
    DEVICE: 'device'
  }

  static FEATURE_PERMISSIONS = {
    // Free tier features - accessible to all
    SPEED_TEST: {
      free: true,
      device: true
    },
    TECH_SUPPORT: {
      free: true,
      device: true
    },
    
    // Dashboard - accessible to all tiers but with different features shown
    DASHBOARD: {
      free: true,
      device: true
    },
    SETTINGS: {
      free: true,
      device: true
    },
    BILLING_SUBSCRIPTION: {
      free: true,
      device: true
    },
    
    // Device-only features - requires device subscription
    FILTER_BLOCKER: {
      free: false,
      device: true
    },
    SITE_MANAGER: {
      free: false,
      device: true
    },
    VPN_CONNECTION: {
      free: false,
      device: true
    },
    DEVICES_MANAGER: {
      free: false,
      device: true
    },
    FIREWALL_RULES: {
      free: false,
      device: true
    },
    NETWORK: {
      free: false,
      device: true
    },
    NETWORK_STATS: {
      free: false,
      device: true
    },
    PORT_FORWARDING: {
      free: false,
      device: true
    },
    AI_CHAT: {
      free: false,
      device: true
    }
  }

  static ROUTE_FEATURE_MAP = {
    'dashboard': 'DASHBOARD',
    'speedtest': 'SPEED_TEST',
    'filterBlocker': 'FILTER_BLOCKER',
    'devicesManager': 'DEVICES_MANAGER',
    'firewallRules': 'FIREWALL_RULES',
    'network': 'NETWORK',
    'networkStats': 'NETWORK_STATS',
    'portForwarding': 'PORT_FORWARDING',
    'siteManager': 'SITE_MANAGER',
    'vpn': 'VPN_CONNECTION',
    'techSupport': 'TECH_SUPPORT',
    'settings': 'SETTINGS',
    'billingSubscription': 'BILLING_SUBSCRIPTION',
    'chat': 'AI_CHAT'
  }

  static canAccessFeature(subscriptionType, featureName, deviceOnline = false) {
    const feature = this.FEATURE_PERMISSIONS[featureName]
    if (!feature) return false

    const permission = feature[subscriptionType]
    
    if (permission === true) return true
    if (permission === false) return false
    if (permission === 'device_required') {
      return subscriptionType === this.SUBSCRIPTION_TYPES.PAID_WITH_DEVICE && deviceOnline
    }
    
    return false
  }

  static canAccessRoute(subscriptionType, routeName, deviceOnline = false) {
    const featureName = this.ROUTE_FEATURE_MAP[routeName]
    if (!featureName) return true // Allow access to unmapped routes
    
    return this.canAccessFeature(subscriptionType, featureName, deviceOnline)
  }

  static getAccessLevel(subscriptionType, featureName, deviceOnline = false) {
    const feature = this.FEATURE_PERMISSIONS[featureName]
    if (!feature) return 'blocked'

    const permission = feature[subscriptionType]
    
    if (permission === true) return 'allowed'
    if (permission === false) return 'blocked'
    if (permission === 'device_required') {
      if (subscriptionType === this.SUBSCRIPTION_TYPES.PAID_WITH_DEVICE) {
        return deviceOnline ? 'allowed' : 'device_offline'
      }
      return 'upgrade_required'
    }
    
    return 'blocked'
  }

  static getUpgradeMessage(subscriptionType, featureName) {
    const accessLevel = this.getAccessLevel(subscriptionType, featureName)
    
    switch (accessLevel) {
      case 'blocked':
        if (subscriptionType === this.SUBSCRIPTION_TYPES.FREE) {
          return 'This feature requires a paid subscription. Upgrade to access all features.'
        }
        return 'This feature is not available with your current subscription.'
      
      case 'upgrade_required':
        return 'This feature requires a device subscription. Upgrade to access device management features.'
      
      case 'device_offline':
        return 'This feature requires your device to be online. Please check your device connection.'
      
      default:
        return ''
    }
  }

  static getAvailableRoutes(subscriptionType, deviceOnline = false) {
    const availableRoutes = []
    
    for (const [routeName] of Object.entries(this.ROUTE_FEATURE_MAP)) {
      if (this.canAccessRoute(subscriptionType, routeName, deviceOnline)) {
        availableRoutes.push(routeName)
      }
    }
    
    return availableRoutes
  }

  static getBlockedRoutes(subscriptionType, deviceOnline = false) {
    const blockedRoutes = []
    
    for (const [routeName, featureName] of Object.entries(this.ROUTE_FEATURE_MAP)) {
      if (!this.canAccessRoute(subscriptionType, routeName, deviceOnline)) {
        blockedRoutes.push({
          route: routeName,
          reason: this.getUpgradeMessage(subscriptionType, featureName)
        })
      }
    }
    
    return blockedRoutes
  }
}

export default SubscriptionPermissionService